// Saved sessions UI — Phase 8 magic-link-lite.
//
// 💾 button in the topbar. Tap → app captures current state → POSTs to
// /api/sessions/save → modal opens with a share URL like
// https://imersivo-ai-interior-playcanvas-vr.vercel.app/?s=ABCD1234 +
// the QR overlay for in-person handoff + Copiar button.
//
// On boot, if `?s=<code>` is in the URL, the app fetches + rehydrates
// before the first frame is painted so the buyer's stored room is the
// first thing they see.

import type { BuyerSession } from '../app/types';
import { showToast } from './toast';
import type { QrPanel } from './qrPanel';

export interface SavedSessionsDeps {
  /** Reads current state for save. */
  capture: () => Omit<BuyerSession, 'sessionCode' | 'savedAt'>;
  /** Applies a loaded state to the running app. */
  apply: (session: BuyerSession) => void;
  /** Reuses the existing QR overlay for the share moment. */
  qr: QrPanel;
}

const QUERY_KEY = 's';
const STORAGE_KEY = 'imersivo:session-code';

export class SavedSessions {
  private readonly deps: SavedSessionsDeps;
  private button: HTMLButtonElement | null = null;
  private currentCode: string | null = null;
  /** Buyer note from the first save / hydrated session. Preserved across
   *  re-saves + background autosaves so they don't blank it out. */
  private currentNote: string | undefined;

  constructor(deps: SavedSessionsDeps) {
    this.deps = deps;
  }

  /** Public accessor for the currently-active session code, if any. Used by
   *  share surfaces (e.g., the WhatsApp button) so a share link keeps the
   *  buyer's saved state instead of just the room URL. */
  getCurrentCode(): string | null {
    return this.currentCode;
  }

  install(): void {
    this.button = document.getElementById('saved-session-button') as HTMLButtonElement | null;
    if (this.button) this.button.addEventListener('click', () => void this.handleSave());
    // Remember the code locally so re-saves in the same session re-use it
    // (the operator's "save" → "save again with one more variant" works).
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) this.currentCode = stored;
    } catch {
      /* ignore */
    }
  }

  /**
   * Called by App early in start() — if the URL has `?s=<code>`, fetches
   * the session and applies it before the first render so the buyer's
   * stored room/variant/yaw is the cold-paint state.
   */
  async maybeHydrateFromUrl(): Promise<boolean> {
    const code = readSessionCodeFromUrl();
    if (!code) return false;
    try {
      const res = await fetch(`/api/sessions/load?code=${encodeURIComponent(code)}`);
      if (res.status === 404) {
        showToast(`Sessão "${code}" não encontrada — abrindo padrão`, { tone: 'info' });
        return false;
      }
      if (!res.ok) {
        showToast(`Falha ao carregar sessão (${res.status})`, { tone: 'error' });
        return false;
      }
      const session = (await res.json()) as BuyerSession;
      this.currentCode = session.sessionCode;
      this.currentNote = session.buyerNote;
      try {
        localStorage.setItem(STORAGE_KEY, session.sessionCode);
      } catch {
        /* ignore */
      }
      this.deps.apply(session);
      const note = session.buyerNote ? ` · ${session.buyerNote}` : '';
      showToast(`Sessão "${code}" restaurada${note}`, { tone: 'info' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`Erro ao carregar sessão: ${msg}`, { tone: 'error' });
      return false;
    }
  }

  /**
   * Silent background re-save of the active session — no prompt, no QR, no
   * toast. No-op until the buyer/rep has saved at least once (so we never
   * mint a code unprompted). Preserves the buyer note. Errors are swallowed;
   * the manual 💾 button is where save failures surface to the user.
   */
  async autosaveIfActive(): Promise<void> {
    if (!this.currentCode) return;
    try {
      const state = this.deps.capture();
      await fetch('/api/sessions/save', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          sessionCode: this.currentCode,
          state: { ...state, buyerNote: this.currentNote },
        }),
      });
    } catch {
      /* background save — stay silent */
    }
  }

  private async handleSave(): Promise<void> {
    const note = this.currentCode
      ? this.currentNote // re-save: keep the note set on the first save
      : (window.prompt('Nome ou nota para esta sessão? (opcional)') ?? '').trim() || undefined;
    this.currentNote = note;
    showToast('Salvando sessão…', { tone: 'info' });
    const state = this.deps.capture();
    try {
      const res = await fetch('/api/sessions/save', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          sessionCode: this.currentCode ?? undefined,
          state: { ...state, buyerNote: note },
        }),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        showToast(`Salvar sessão: erro ${res.status} — ${errBody.error ?? 'desconhecido'}`, { tone: 'error' });
        return;
      }
      const body = (await res.json()) as { sessionCode: string };
      this.currentCode = body.sessionCode;
      try {
        localStorage.setItem(STORAGE_KEY, body.sessionCode);
      } catch {
        /* ignore */
      }
      const shareUrl = buildShareUrl(body.sessionCode);
      // Update the browser URL so a copy/paste of the address-bar URL works
      // even without clicking the share button.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set(QUERY_KEY, body.sessionCode);
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* ignore */
      }
      showToast(`Sessão salva: ${body.sessionCode}`, { tone: 'info' });
      void this.deps.qr.show(shareUrl);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`Falha de rede ao salvar: ${msg}`, { tone: 'error' });
    }
  }
}

function readSessionCodeFromUrl(): string | null {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get(QUERY_KEY);
    if (code && /^[a-z0-9]{8}$/i.test(code)) return code.toLowerCase();
  } catch {
    /* ignore */
  }
  return null;
}

function buildShareUrl(code: string): string {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(QUERY_KEY, code);
    return url.toString();
  } catch {
    return `${window.location.origin}/?s=${code}`;
  }
}
